"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icons } from "@/components/Icons";
import { Badge, Button, Card, SectionHeader, StickyBar } from "@/components/ui";
import { FoodImg } from "@/components/FoodImg";
import { imageThumbnailUrl, normalizeImageUrl } from "@/lib/imageUrls";
import saltIcon from "@/assets/Groupsalti.svg";
import flameIcon from "@/assets/Frame.svg";
import { SignInModal } from "@/components/SignInModal";
import { useCart } from "@/store/cart";
import { useTable } from "@/store/table";
import { useAuth } from "@/store/auth";
import { useFulfillment } from "@/store/fulfillment";
import { useMenu, type UiMenuItem } from "@/hooks/useMenu";
import { useOrders } from "@/hooks/useOrders";
import { useAddresses } from "@/hooks/useAddresses";
import { isTerminalStatus } from "@/hooks/useOrder";
import { useFormatPrice } from "@/hooks/useFormatPrice";
import { useMinOrder, useSyncNearestBranch } from "@/hooks/useBranchInfo";
import { useBranchOpen } from "@/hooks/useBranchOpen";
import { useBranch } from "@/store/branch";
import { Price } from "@/components/Price";
import { StorefrontNote } from "@/components/StorefrontNote";
import {
  usePromotions,
  usePromotionIdentity,
  getPromotionName,
  getPromotionDescription,
  isPromotionAutoApplied,
} from "@/hooks/usePromotions";
import { branchesApi, promotionsApi } from "@/api/services";
import { useTillPricing } from "@/hooks/useTillPricing";
import { groupCartApi } from "@/api/group-cart";
import { useGroupCart } from "@/store/group-cart";
import { quickAdd } from "@/lib/quick-add";
import { TableBillBanner } from "@/components/dinein/TableBillBanner";
import { useActiveTableOrder } from "@/hooks/useActiveTableOrder";
import { GroupBanner } from "@/components/group/GroupBanner";
import { GroupOfferBanner } from "@/components/group/GroupOfferBanner";
import { WelcomeOfferBanner } from "@/components/loyalty/WelcomeOfferBanner";
import { GroupSheet } from "@/components/group/GroupSheet";
import { GroupStartSheet } from "@/components/group/GroupStartSheet";
import { HomeChallengesWidget } from "@/components/loyalty/HomeChallengesWidget";
import { HomeOffersCarousel } from "@/components/offers/HomeOffersCarousel";
import { useOffersInbox } from "@/hooks/useOffers";
import { useAppMode } from "@/hooks/useAppMode";
import { useShortlist } from "@/store/shortlist";
import { ShortlistBar } from "@/components/ShortlistBar";
import { scopedStorageKey } from "@/lib/storageScope";
import { readParticipant } from "@/lib/participant-token";
import {
  addressDisplayLine,
  branchAddressLine,
  branchDisplayName,
  branchIdentifier,
  branchSupports,
  type FulfillmentMode,
} from "@/lib/fulfillment";
import {
  OrderTypeEnum,
  type OrderOutput,
  type AppSchemasCustomerAddress,
  type BranchConfig,
} from "@/api/online-ordering-service/model";
import { reviewApi, type BranchReviewSummary, type ItemReviewSummary } from "@/api/reviewApi";
import { storefrontName } from "@/lib/storefrontName";

// Local alias so the existing JSX (`item: MenuItem`) keeps working.
type MenuItem = UiMenuItem;

/**
 * Public per-item rating badges (M4). Fetches summaries ONCE for the visible
 * item set (not per-card) and degrades silently to an empty map on failure —
 * a reviews outage must never affect the menu list.
 */
function useItemRatingsMap(itemIds: string[]): Record<string, ItemReviewSummary> {
  const [ratings, setRatings] = useState<Record<string, ItemReviewSummary>>({});
  // Stable key so the effect only re-fires when the actual id set changes,
  // not on every render (allItems is recomputed frequently upstream).
  const idsKey = itemIds.slice().sort().join(",");

  useEffect(() => {
    let cancelled = false;
    if (!idsKey) {
      setRatings({});
      return;
    }
    reviewApi
      .getItemsReviewsSummary(idsKey.split(","))
      .then((res) => {
        if (!cancelled) setRatings(res?.summaries ?? {});
      })
      .catch(() => {
        if (!cancelled) setRatings({});
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  return ratings;
}

/** Public branch rating badge (M4). Degrades silently to null on failure. */
function useBranchReviewSummary(branchId: string | null | undefined): BranchReviewSummary | null {
  const [summary, setSummary] = useState<BranchReviewSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!branchId) {
      setSummary(null);
      return;
    }
    reviewApi
      .getBranchReviewsSummary(branchId)
      .then((res) => {
        if (!cancelled) setSummary(res ?? null);
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [branchId]);

  return summary;
}

const iconBtn: React.CSSProperties = {
  width: 38,
  height: 38,
  borderRadius: 12,
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  display: "grid",
  placeItems: "center",
  color: "var(--ink-1)",
  cursor: "pointer",
  flexShrink: 0,
};

type OrderType = "delivery" | "pickup" | "dineIn";
const HOME_SCROLL_KEY = "v2.home.scrollTop";
const RECENT_SEARCHES_KEY = "v2.home.recentSearches";
const RECENT_SEARCHES_MAX = 5;
// 

function itemHasVariantsOrModifiers(item: MenuItem): boolean {
  const hasVariants = (item.raw?.variantGroups?.length ?? 0) > 0;
  const hasModifiers = (item.raw?.modifierGroups?.length ?? 0) > 0;
  return hasVariants || hasModifiers;
}

function shouldShowPriceAfterOptionsNote(item: MenuItem): boolean {
  const hasVariants = (item.raw?.variantGroups?.length ?? 0) > 0;
  return hasVariants || (item.price <= 0 && itemHasVariantsOrModifiers(item));
}

function readRecentSearches(slug: string | null | undefined): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(scopedStorageKey(RECENT_SEARCHES_KEY, slug));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((s): s is string => typeof s === "string" && s.length > 0)
      : [];
  } catch {
    return [];
  }
}

function pushRecentSearch(slug: string | null | undefined, q: string): void {
  if (typeof window === "undefined") return;
  const trimmed = q.trim();
  if (!trimmed) return;
  try {
    const key = scopedStorageKey(RECENT_SEARCHES_KEY, slug);
    const current = readRecentSearches(slug).filter(
      (r) => r.toLowerCase() !== trimmed.toLowerCase(),
    );
    const next = [trimmed, ...current].slice(0, RECENT_SEARCHES_MAX);
    window.sessionStorage.setItem(key, JSON.stringify(next));
  } catch {
    // ignore storage errors
  }
}

export function HomeScreen() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const toggleLang = () => i18n.changeLanguage(i18n.language === "ar" ? "en" : "ar");
  const { cart, cartCount, cartTotal, clearCart, setAppliedPromotion, appliedPromotion } = useCart();
  // Menu-only links (menu link `type: digitalMenu`) browse + shortlist only.
  // `isOrdering` is false while config is still resolving, so ordering chrome
  // never flashes before we know which kind of link this is.
  const { isMenuOnly, isOrdering } = useAppMode();
  const { shortlistCount } = useShortlist();
  const { activeCount: offersBadgeCount } = useOffersInbox();
  const { isDineIn, tableInfo } = useTable();
  const groupCart = useGroupCart();
  const [groupSheetOpen, setGroupSheetOpen] = useState(false);
  const [groupStartSheetOpen, setGroupStartSheetOpen] = useState(false);
  const probedGroupRef = useRef<string | null>(null);
  const { isLoggedIn } = useAuth();
  const {
    mode,
    setMode,
    selectedAddressId,
    setSelectedAddressId,
    manualAddress,
  } = useFulfillment();
  const { addresses, loading: addressesLoading } = useAddresses();
  const { orders } = useOrders();
  const { categories, items: allItems, loading, error, menuTaxExempt } = useMenu();
  const visibleItemIds = useMemo(() => allItems.map((it) => it.id), [allItems]);
  const itemRatings = useItemRatingsMap(visibleItemIds);
  const { format } = useFormatPrice();
  const { visiblePromotions, promotions } = usePromotions();
  const promoIdentity = usePromotionIdentity();
  const { isClosed, opensAt } = useBranchOpen();
  const minOrder = useMinOrder();
  const {
    branchConfig,
    branch,
    branchId,
    branchSlug,
    selectedBranchId,
    selectBranch,
    requiresBranchSelection,
  } = useBranch();
  useSyncNearestBranch();
  // Before the config loads, branchId falls back to the URL slug (the menu
  // link id), which is not a branch: wait for the config.
  const branchReviewSummary = useBranchReviewSummary(branchConfig ? branchId : null);
  const carouselImages = useMemo<string[]>(() => {
    const cfg = branchConfig as
      | (typeof branchConfig & {
          imageArray?: string[] | null;
          carouselImageUrls?: string[] | null;
          branches?: Array<{ promotionalImages?: string[] | null }>;
        })
      | null;
    const fromConfig =
      cfg?.imageArray ??
      cfg?.carouselImageUrls ??
      (branch as { promotionalImages?: string[] | null } | null)
        ?.promotionalImages ??
      cfg?.branches?.[0]?.promotionalImages ??
      null;
    return Array.isArray(fromConfig) ? fromConfig.filter(Boolean) : [];
  }, [branch, branchConfig]);
  const carouselSlideCount = carouselImages.length;
  const [activeCat, setActiveCat] = useState<string>("");
  const [q, setQ] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [chooserMode, setChooserMode] = useState<FulfillmentMode | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [signInOpen, setSignInOpen] = useState(false);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const chipRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const scrollRoot = useRef<HTMLElement | null>(null);
  const homeRef = useRef<HTMLDivElement | null>(null);
  const getScrollRoot = (): HTMLElement | null => {
    if (scrollRoot.current) return scrollRoot.current;
    const resolved = homeRef.current?.closest<HTMLElement>(".phone-content") ?? document.querySelector<HTMLElement>(".phone-content");
    if (resolved) scrollRoot.current = resolved;
    return resolved;
  };
  const restoredScrollRef = useRef(false);
  const isClickScrollingRef = useRef(false);
  const lastModeRef = useRef<FulfillmentMode | null>(null);
  const lastBranchRef = useRef<string | null>(null);
  const orderType: OrderType = isDineIn ? "dineIn" : mode;
  const lang = i18n.language || "en";
  const selectedAddress = useMemo(
    () => (selectedAddressId ? addresses.find((a) => a.id === selectedAddressId) : null) ?? manualAddress,
    [addresses, selectedAddressId, manualAddress],
  );
  const chooserBranches = useMemo(
    () => (branchConfig?.branches ?? []).filter((b) => isMenuOnly || branchSupports(b, "pickup")),
    [branchConfig?.branches, isMenuOnly],
  );
  const activeBranchName = branchDisplayName(branch, lang);
  const activeBranchAddress = branchAddressLine(branch, lang);
  const homeScrollKey = useMemo(
    () => scopedStorageKey(HOME_SCROLL_KEY, branchSlug),
    [branchSlug],
  );
  const recommendedItems = useMemo(() => {
    const available = allItems.filter((item) => item.isAvailable);
    const picked = available.filter((item) => item.popular).slice(0, 8);
    return (picked.length > 0 ? picked : available.slice(0, 8)).filter(Boolean);
  }, [allItems]);

  const deliveryHeaderLine = selectedAddress
    ? addressDisplayLine(selectedAddress)
    : isLoggedIn
    ? t("v2.home.choose_delivery_address")
    : t("v2.home.sign_in_for_delivery");
  const pickupHeaderLine =
    (!requiresBranchSelection && activeBranchName) ? activeBranchName : t("v2.home.choose_pickup_branch");

  useEffect(() => {
    if (lastModeRef.current == null) {
      lastModeRef.current = mode;
      return;
    }
    if (lastModeRef.current !== mode && cartCount > 0) {
      clearCart();
    }
    lastModeRef.current = mode;
  }, [mode, cartCount, clearCart]);

  useEffect(() => {
    const currentBranch = selectedBranchId ?? branchId ?? null;
    if (lastBranchRef.current == null) {
      lastBranchRef.current = currentBranch;
      return;
    }
    if (
      currentBranch &&
      lastBranchRef.current &&
      currentBranch !== lastBranchRef.current &&
      cartCount > 0
    ) {
      clearCart();
    }
    lastBranchRef.current = currentBranch;
  }, [selectedBranchId, branchId, cartCount, clearCart]);

  // Categories come from the API directly; each already has its items.
  const sections = categories;

  // Initialise activeCat once we know which categories exist (avoids the
  // hard-coded mock fallback).
  useEffect(() => {
    if (sections.length === 0 && activeCat) {
      setActiveCat("");
      return;
    }
    if (sections.length > 0 && (!activeCat || !sections.some((s) => s.id === activeCat))) {
      setActiveCat(sections[0].id);
    }
  }, [sections, activeCat]);

  useEffect(() => {
    if (!isLoggedIn || addressesLoading || addresses.length === 0) return;
    // If we already have a saved selection or a manual pin from the map, don't overwrite it.
    if (selectedAddressId || manualAddress) {
      const found = addresses.find((a) => a.id === selectedAddressId);
      if (found || manualAddress) return;
    }
    const primary = addresses.find((a) => a.isPrimary) ?? addresses[0];
    setSelectedAddressId(primary.id);
  }, [
    addresses,
    addressesLoading,
    isLoggedIn,
    selectedAddressId,
    manualAddress,
    setSelectedAddressId,
  ]);

  const filtered = useMemo(() => {
    if (!q) return allItems;
    const s = q.toLowerCase();
    return allItems.filter(
      (m) => m.name.toLowerCase().includes(s) || m.desc.toLowerCase().includes(s)
    );
  }, [q, allItems]);

  // Open the fulfillment chooser if redirected here with `?openFulfillment=…`.
  useEffect(() => {
    const want = searchParams.get("openFulfillment");
    if (want === "pickup" || want === "delivery") {
      setChooserMode(want as FulfillmentMode);
      const next = new URLSearchParams(searchParams);
      next.delete("openFulfillment");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link "Use now" from the offers inbox / carousel: `?code=XYZ` lands
  // here, we validate it against the branch's active promotions and stage it on
  // the cart via the existing promo-code path (mirrors CartScreen.onApply), then
  // strip the param so a reload doesn't re-apply. Fail-soft: an invalid or
  // not-yet-loadable code is silently ignored — never blocks the menu.
  const appliedCodeRef = useRef<string | null>(null);
  useEffect(() => {
    const code = searchParams.get("code")?.trim();
    if (!code || !branchId) return;
    if (appliedCodeRef.current === code) return;
    appliedCodeRef.current = code;

    let cancelled = false;
    promotionsApi
      .validateCode(branchId, code, { orderType, ...promoIdentity })
      .then((match) => {
        if (cancelled || !match) return;
        setAppliedPromotion(match);
      })
      .catch((err) => {
        console.warn("[home] deep-linked promo code apply failed", err);
      })
      .finally(() => {
        if (cancelled) return;
        const next = new URLSearchParams(searchParams);
        next.delete("code");
        setSearchParams(next, { replace: true });
      });

    return () => {
      cancelled = true;
    };
  }, [searchParams, branchId, orderType, setAppliedPromotion, setSearchParams]);

  // The promotions the till applies to this cart (every eligible auto-apply
  // promotion plus an entered code's), priced by priceCart — no separate
  // "best promotion" pick here, so Home never promises what checkout won't give.
  const { appliedPromotions: tillApplied } = useTillPricing({ menuItems: allItems, menuTaxExempt, promotions });
  const shownPromotion = tillApplied[0] ?? appliedPromotion ?? null;

  // Carousel autoplay — only run when there is more than one image to cycle.
  useEffect(() => {
    if (searchActive || carouselSlideCount <= 1) return;
    const t = setInterval(
      () => setCarouselIdx((i) => (i + 1) % carouselSlideCount),
      4500,
    );
    return () => clearInterval(t);
  }, [searchActive, carouselSlideCount]);

  useEffect(() => {
    if (carouselIdx >= carouselSlideCount) setCarouselIdx(0);
  }, [carouselIdx, carouselSlideCount]);

  useEffect(() => {
    getScrollRoot();
  }, []);

  const saveHomeScroll = () => {
    const root = getScrollRoot();
    if (!root) return;
    window.sessionStorage.setItem(homeScrollKey, String(root.scrollTop));
  };

  useEffect(() => {
    if (searchActive || restoredScrollRef.current || sections.length === 0) return;
    const root = getScrollRoot();
    const raw = window.sessionStorage.getItem(homeScrollKey);
    restoredScrollRef.current = true;
    if (!root || raw == null) return;
    const top = Number(raw);
    if (!Number.isFinite(top) || top <= 0) return;
    window.requestAnimationFrame(() => {
      root.scrollTo({ top, behavior: "auto" });
    });
  }, [homeScrollKey, searchActive, sections.length]);

  // v1 checks for an active dine-in order when a QR table URL is opened. If
  // this table already has an order we auto-open the live bill — but only
  // ONCE per order (marker persisted in sessionStorage, so it survives the
  // HomeScreen remount that every "back to /" from DineInOrderScreen causes).
  // After that the bill stays reachable via the non-blocking TableBillBanner
  // rendered below, and the guest is free to browse the menu.
  const { activeOrderId: activeTableOrderId } = useActiveTableOrder({
    enabled: isDineIn,
    branchId: tableInfo?.branchId ?? branchId,
    tableId: tableInfo?.tableId,
  });

  // QR scan into a table that already has an active group cart we're not in
  // → bounce to the join screen so the guest can pick a name and join. Also
  // honour `?inviteCode=ABCD` in the URL: that's a shared invite link, the
  // user shouldn't have to find and click the eat-together banner manually.
  // We skip the probe when the store is already in an `active`/`fired` state
  // for the same cart (covers reload + invite-link re-entry).
  const inviteCodeParam = searchParams.get("inviteCode")?.toUpperCase().trim() || null;
  useEffect(() => {
    if (groupCart.status !== "idle") return;
    // While hydrate from sessionStorage is racing, the store is briefly
    // 'idle' even though we already have a participant. Don't yank the user
    // to the join screen if we're about to land them back in their group.
    if (readParticipant()) return;

    // Shared invite link → straight to join screen, preserving the code.
    if (inviteCodeParam) {
      navigate(`/group/join?inviteCode=${encodeURIComponent(inviteCodeParam)}`);
      return;
    }

    if (!isDineIn || !tableInfo?.tableId) return;
    const resolvedBranchId = tableInfo.branchId ?? branchId;
    if (!resolvedBranchId) return;
    const key = `${resolvedBranchId}:${tableInfo.tableId}`;
    if (probedGroupRef.current === key) return;
    probedGroupRef.current = key;

    let cancelled = false;
    groupCartApi
      .probeActive(resolvedBranchId, tableInfo.tableId)
      .then((result) => {
        if (cancelled || !result) return;
        navigate(`/group/join?groupCartId=${encodeURIComponent(result.groupCartId)}`);
      })
      .catch((err) => {
        console.warn("[home] group cart probe failed", err);
      });

    return () => {
      cancelled = true;
    };
  }, [
    branchId,
    groupCart.status,
    inviteCodeParam,
    isDineIn,
    navigate,
    tableInfo?.branchId,
    tableInfo?.tableId,
  ]);

  // Scroll-spy
  useEffect(() => {
    const root = getScrollRoot();
    if (!root) return;
    const onScroll = () => {
      if (isClickScrollingRef.current) return;

      // Bottom boundary detection
      const isAtBottom = Math.abs(root.scrollHeight - root.clientHeight - root.scrollTop) < 15;
      if (isAtBottom && sections.length > 0) {
        const lastSectionId = sections[sections.length - 1].id;
        if (activeCat !== lastSectionId) {
          setActiveCat(lastSectionId);
        }
        return;
      }

      const stickyOffset = 180;
      let current = sections[0]?.id;
      for (const s of sections) {
        const el = sectionRefs.current[s.id];
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        if (rect.top - rootRect.top <= stickyOffset) {
          current = s.id;
        } else {
          break;
        }
      }
      if (current && current !== activeCat) setActiveCat(current);
    };
    root.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => root.removeEventListener("scroll", onScroll);
  }, [sections, activeCat]);

  useEffect(() => {
    const chip = chipRefs.current[activeCat];
    if (chip) chip.scrollIntoView({ block: "nearest", inline: "center", behavior: "auto" });
  }, [activeCat]);

  const jumpTo = (id: string) => {
    const root = document.querySelector<HTMLElement>(".phone-content");
    const el = sectionRefs.current[id];
    console.log("[jumpTo]", { id, root: !!root, el: !!el, sectionKeys: Object.keys(sectionRefs.current) });
    if (!el) return;
    isClickScrollingRef.current = true;
    setActiveCat(id);

    if (root) {
      const rootRect = root.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const target = Math.max(0, root.scrollTop + elRect.top - rootRect.top - 110);
      console.log("[jumpTo scroll]", { scrollTop: root.scrollTop, elTop: elRect.top, rootTop: rootRect.top, target, scrollHeight: root.scrollHeight, clientHeight: root.clientHeight });
      root.scrollTop = target;
    } else {
      el.scrollIntoView({ block: "start" });
    }

    setTimeout(() => {
      isClickScrollingRef.current = false;
    }, 800);
  };

  const activeOrders = useMemo(() => {
    if (!isLoggedIn) return [];
    return orders
      .filter((o) => !isTerminalStatus(o.status))
      .slice()
      .sort((a, b) => {
        const ta = a.createdAt ? Date.parse(a.createdAt) : 0;
        const tb = b.createdAt ? Date.parse(b.createdAt) : 0;
        return tb - ta;
      });
  }, [isLoggedIn, orders]);

  const openActiveOrder = (order: OrderOutput) => {
    if (!order.id) return;
    const encodedId = encodeURIComponent(order.id);
    navigate(
      order.orderType === OrderTypeEnum.DineIn
        ? `/dine-in-order/${encodedId}`
        : `/tracking/${encodedId}`,
    );
  };

  const onOpenItem = (it: MenuItem) => {
    if (!searchActive) saveHomeScroll();
    // Digital-menu links browse against the preview branch: the map picker
    // is an ordering route and would redirect straight back home.
    if (requiresBranchSelection && !isMenuOnly) {
      navigate("/specify-location?returnTo=home");
      return;
    }
    navigate(`/item/${it.id}`);
  };

  const onSelectDeliveryAddress = async (address: AppSchemasCustomerAddress) => {
    setMode("delivery");
    setSelectedAddressId(address.id);
    setChooserMode(null);
    const coords = address.coordinates;
    if (!coords || !branchConfig?.urlPath) return;
    try {
      const nearest = await branchesApi.findNearest(
        coords.latitude,
        coords.longitude,
        branchConfig.urlPath,
        { limit: 1, serviceType: "delivery" },
      );
      const nearestBranch = nearest.branches?.[0] ?? null;
      if (nearestBranch?.id) {
        selectBranch(nearestBranch.id, nearestBranch);
      }
    } catch (err) {
      console.warn("[home] nearest branch lookup failed for saved address", err);
    }
  };

  if (searchActive) {
    return (
      <SearchOverlay
        q={q}
        setQ={setQ}
        onClose={() => {
          setSearchActive(false);
          setQ("");
        }}
        onOpenItem={(it) => {
          if (q.trim()) pushRecentSearch(branchSlug, q);
          onOpenItem(it);
        }}
        results={filtered}
        allItems={allItems}
        categories={sections}
        branchClosed={isClosed}
        hidePrices={requiresBranchSelection}
        itemRatings={itemRatings}
        branchSlug={branchSlug}
        onCategorySelect={(id) => {
          setSearchActive(false);
          setQ("");
          window.requestAnimationFrame(() => jumpTo(id));
          window.setTimeout(() => jumpTo(id), 80);
        }}
      />
    );
  }

  return (
    <div
      ref={homeRef}
      style={{
        // No bottom nav in menu-only mode, so only the shortlist bar needs
        // clearing.
        paddingBottom: isMenuOnly
          ? shortlistCount > 0
            ? 96
            : 24
          : cartCount > 0
            ? 154
            : 76,
      }}
    >
      {/* Group cart surfaces — render at the very top so they sit above the
          closed-branch banner and any hero. Three states:
            - active group → dark GroupBanner that opens GroupSheet
            - dine-in but no group → GroupOfferBanner that opens GroupStartSheet
            - no table context → nothing */}
      {/* Open table bill — persistent, non-blocking entry point. Home no
          longer force-redirects here on every mount (see useActiveTableOrder),
          so this banner is how the diner gets back to the live bill. */}
      {isDineIn && activeTableOrderId && (
        <TableBillBanner
          onTap={() => navigate(`/dine-in-order/${activeTableOrderId}`)}
        />
      )}

      {/* First-order earn rule (e.g. 100% cashback) for signed-out visitors. */}
      {!isLoggedIn && !isDineIn && <WelcomeOfferBanner />}

      {isOrdering &&
        (groupCart.status === "active" && groupCart.cart ? (
          <GroupBanner
            participants={groupCart.cart.participants}
            onTap={() => setGroupSheetOpen(true)}
          />
        ) : (
          <GroupOfferBanner onTap={() => setGroupStartSheetOpen(true)} />
        ))}

      <GroupSheet
        open={groupSheetOpen}
        onClose={() => setGroupSheetOpen(false)}
      />
      <GroupStartSheet
        open={groupStartSheetOpen}
        onClose={() => setGroupStartSheetOpen(false)}
        onCreated={() => setGroupSheetOpen(true)}
      />

      {/* Branch closed banner — ordering hours say nothing about whether the
          menu is worth reading, so it is suppressed on menu-only links. */}
      {isMenuOnly &&
        (branchConfig as { orderingUnavailableReason?: string | null } | null)?.orderingUnavailableReason === "plan" && (
          <div
            role="status"
            data-testid="ordering-unavailable-plan"
            style={{
              margin: "8px 16px 0",
              padding: "10px 12px",
              borderRadius: 12,
              background: "var(--surface-2, #f4f4f5)",
              color: "var(--ink-2, #3f3f46)",
              fontSize: 13,
              lineHeight: 1.4,
            }}
          >
            {t("v2.menu_only.ordering_unavailable")}
          </div>
        )}
      {isClosed && !isMenuOnly && (
        <div
          style={{
            background: "#fff7ed",
            borderBottom: "1px solid #fed7aa",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              background: "#fed7aa",
              display: "grid",
              placeItems: "center",
              color: "#9a3412",
              flexShrink: 0,
            }}
          >
            <Icons.clock size={14} />
          </div>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 13,
              fontWeight: 600,
              color: "#9a3412",
            }}
          >
            {opensAt ? t("v2.home.closed_opens_at", { time: opensAt }) : t("v2.home.closed")}
          </div>
        </div>
      )}

      {/* Merchant-authored storefront note (e.g. a tax/service-charge
          disclaimer). Self-hides when the menu link has no note. */}
      <StorefrontNote variant="banner" />

      {/* Active orders banner */}
      {isOrdering && activeOrders.length > 0 && (
        <div
          style={{
            background: "var(--surface-2)",
            borderBottom: "1px solid var(--border)",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              background: "var(--primary-100)",
              display: "grid",
              placeItems: "center",
              color: "var(--primary-700)",
            }}
          >
            <Icons.bell size={14} />
          </div>
          <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontSize: 13,
                color: "var(--ink-1)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {t("v2.home.order_being_prepared")}
            </span>
            {activeOrders[0].orderType && (
              <Badge tone="neutral">{activeOrders[0].orderType}</Badge>
            )}
          </div>
          <button
            style={{
              padding: "8px 14px",
              borderRadius: "var(--radius-chip)",
              border: 0,
              fontSize: 13,
              fontWeight: 600,
              background: "var(--primary-600)",
              color: "#fff",
              cursor: activeOrders[0].id ? "pointer" : "not-allowed",
              fontFamily: "inherit",
              opacity: activeOrders[0].id ? 1 : 0.65,
            }}
            disabled={!activeOrders[0].id}
            onClick={() => openActiveOrder(activeOrders[0])}
          >
            {t("v2.home.view_status")}
          </button>
        </div>
      )}

      {/* Header */}
      <div style={{ background: "var(--surface-1)", padding: "14px 16px 14px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          {/* Fulfillment/location chooser. On a menu-only link there is no
              fulfillment to choose, so the branch is shown as plain text. */}
          {isOrdering && (
            <button
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flex: 1,
                background: "transparent",
                border: 0,
                textAlign: "start",
                padding: 0,
                cursor: "pointer",
                color: "var(--ink-1)",
                fontFamily: "inherit",
                minWidth: 0,
              }}
              onClick={() => {
                if (orderType === "dineIn") return;
                if (orderType === "delivery" && !isLoggedIn) {
                  setSignInOpen(true);
                  return;
                }
                setChooserMode(orderType);
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "var(--radius-btn)",
                  background: "var(--primary-50)",
                  display: "grid",
                  placeItems: "center",
                  color: "var(--primary-700)",
                  flexShrink: 0,
                }}
              >
                {orderType === "dineIn" ? <Icons.store size={16} /> : <Icons.pin size={16} />}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--ink-3)",
                    fontWeight: 500,
                    letterSpacing: 0.2,
                    textTransform: "uppercase",
                  }}
                >
                  {orderType === "delivery"
                    ? t("home.delivery_location")
                    : orderType === "pickup"
                    ? t("home.pickup_from")
                    : t("home.table")}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: 1.2,
                    marginTop: 2,
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: 200,
                    }}
                  >
                    {orderType === "dineIn"
                      ? tableInfo?.tableName ??
                        (tableInfo?.tableId
                          ? t("v2.checkout.table_short", { id: tableInfo.tableId.slice(0, 4) })
                          : t("v2.checkout.table_default"))
                      : orderType === "delivery"
                      ? deliveryHeaderLine
                      : pickupHeaderLine}
                  </span>
                  {orderType !== "dineIn" && <Icons.chevronD size={14} style={{ flexShrink: 0 }} />}
                </div>
              </div>
            </button>
          )}
          {isMenuOnly && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "var(--ink-1)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {(branchConfig ? storefrontName(branchConfig) : null) ??
                  activeBranchName ??
                  ""}
              </div>
            </div>
          )}

          {isOrdering && (
          <button
            aria-label={t("v2.offers.title")}
            style={{ ...iconBtn, position: "relative" }}
            onClick={() => navigate("/offers")}
          >
            <Icons.ticket size={18} />
            {offersBadgeCount > 0 && (
              <span
                className="num"
                aria-hidden
                style={{
                  position: "absolute",
                  top: -4,
                  insetInlineEnd: -4,
                  minWidth: 17,
                  height: 17,
                  padding: "0 4px",
                  borderRadius: 999,
                  background: "var(--primary-600)",
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 800,
                  display: "grid",
                  placeItems: "center",
                  border: "2px solid var(--surface-1)",
                }}
              >
                {offersBadgeCount > 9 ? "9+" : offersBadgeCount}
              </span>
            )}
          </button>
          )}
          <button aria-label={t("v2.common.language")} style={iconBtn} onClick={toggleLang}>
            <Icons.globe size={18} />
          </button>
          {isOrdering && (
            <button
              aria-label={t("v2.common.profile")}
              style={iconBtn}
              onClick={() => navigate("/profile")}
            >
              <Icons.user size={18} />
            </button>
          )}
        </div>

        {isOrdering && orderType !== "dineIn" && (
          <div
            className="order-type-toggle"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 4,
              background: "var(--surface-2)",
              borderRadius: "var(--radius-chip)",
              padding: 4,
              border: "1px solid var(--border)",
            }}
          >
            {(["delivery", "pickup"] as const).map((k) => (
              <button
                key={k}
                onClick={() => {
                  // Delivery requires an account so we can fetch saved addresses.
                  // Don't flip the mode until the user is signed in — otherwise
                  // they end up in a delivery-without-address dead-end on cart.
                  if (k === "delivery" && !isLoggedIn) {
                    setSignInOpen(true);
                    return;
                  }
                  setMode(k);
                }}
                style={{
                  height: 36,
                  borderRadius: "var(--radius-chip)",
                  border: 0,
                  fontWeight: 600,
                  fontSize: 13,
                  background: orderType === k ? "var(--surface-1)" : "transparent",
                  color: orderType === k ? "var(--ink-1)" : "var(--ink-2)",
                  boxShadow: orderType === k ? "0 1px 2px rgba(0,0,0,0.07)" : "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                }}
              >
                {k === "delivery" ? <Icons.bike size={14} /> : <Icons.bag size={14} />}
                {k === "delivery" ? t("home.delivery") : t("home.pickup")}
              </button>
            ))}
          </div>
        )}
      </div>

      {branch && (
        <ActiveBranchStrip
          name={activeBranchName || branchIdentifier(branch)}
          address={activeBranchAddress}
          isPreview={requiresBranchSelection}
          isClosed={isClosed}
          minOrder={orderType === "delivery" ? minOrder : 0}
          hideChange={isDineIn}
          reviewSummary={branchReviewSummary}
          onChange={() => {
            if (orderType === "pickup" || isMenuOnly) {
              setChooserMode("pickup");
            } else {
              navigate("/specify-location?returnTo=home");
            }
          }}
        />
      )}

      {/* Activated Promotion */}
      {!requiresBranchSelection && shownPromotion && (
        <div style={{ padding: "12px 16px 0" }}>
          <Card
            padding={0}
            style={{
              background: "var(--primary-50)",
              border: "1px dashed var(--primary-300)",
            }}
          >
            <div style={{ padding: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <div style={{ width: 24, height: 24, borderRadius: 999, background: "var(--primary-600)", display: "grid", placeItems: "center", color: "#fff" }}>
                  <Icons.ticket size={14} />
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--primary-800)" }}>
                  {t("v2.home.automatically_applied")}
                </div>
              </div>
              <div style={{ fontSize: 14, color: "var(--ink-1)", fontWeight: 600 }}>
                {tillApplied.length > 1
                  ? tillApplied.map((p) => getPromotionName(p, i18n.language) || p.promoCode).filter(Boolean).join(" + ")
                  : getPromotionName(shownPromotion, i18n.language) || shownPromotion.promoCode}
              </div>
              {tillApplied.length <= 1 && getPromotionDescription(shownPromotion, i18n.language) && (
                <div style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 4 }}>
                  {getPromotionDescription(shownPromotion, i18n.language)}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Special offers */}
      {isOrdering && !requiresBranchSelection && !shownPromotion && visiblePromotions.length > 0 && (() => {
        const promo = visiblePromotions[0];
        const promoName =
          getPromotionName(promo, i18n.language) || t("home.special_offers");
        const promoDesc = getPromotionDescription(promo, i18n.language);
        const autoApplied = isPromotionAutoApplied(promo);
        const minOrder = promo.criteria?.minOrderValue ?? null;
        const moreCount = visiblePromotions.length - 1;
        return (
          <div style={{ padding: "12px 16px 0" }}>
            <Card
              padding={0}
              style={{
                background:
                  "linear-gradient(45deg, var(--hero-from) 50%, var(--hero-to) 120%)",
                border: 0,
              }}
            >
              <div style={{ padding: 14 }}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}
                >
                  <Icons.flame size={16} style={{ color: "#dc2626" }} />
                  <div className="display" style={{ fontSize: 15, color: "var(--ink-1)" }}>
                    {t("home.special_offers")}
                  </div>
                  {moreCount > 0 && (
                    <span style={{ fontSize: 12, color: "var(--ink-2)", fontWeight: 500 }}>
                      {t("v2.home.more_count", { count: moreCount })}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div className="display" style={{ fontSize: 14, color: "var(--ink-1)" }}>
                        {promoName}
                      </div>
                      {autoApplied && (
                        <Badge
                          tone="primary"
                          style={{
                            marginTop: 6,
                            background: "var(--primary-200)",
                            color: "var(--primary-800)",
                          }}
                        >
                          {t("v2.home.automatically_applied")}
                        </Badge>
                      )}
                      {promo.promoCode && !autoApplied && (
                        <Badge
                          tone="primary"
                          style={{
                            marginTop: 6,
                            background: "var(--primary-200)",
                            color: "var(--primary-800)",
                          }}
                        >
                          Code: {promo.promoCode}
                        </Badge>
                      )}
                    </div>
                    {minOrder != null && minOrder > 0 && (
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "var(--ink-1)",
                          textAlign: "right",
                          flexShrink: 0,
                        }}
                      >
                        {t("v2.home.min_order", { amount: format(minOrder) })}
                      </div>
                    )}
                  </div>
                  {promoDesc && (
                    <div style={{ fontSize: 13, color: "var(--ink-1)", lineHeight: 1.4 }}>
                      {promoDesc}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        );
      })()}

      {/* Carousel — only render if the merchant configured promo images. */}
      {carouselImages.length > 0 && (
        <div style={{ padding: "12px 16px 8px" }}>
          <div
            style={{
              position: "relative",
              borderRadius: "var(--radius-card)",
              overflow: "hidden",
              border: "1px solid var(--border)",
            }}
          >
            <CarouselImage
              src={carouselImages[carouselIdx % carouselImages.length]}
              alt={t("v2.home.promo_alt", { n: carouselIdx + 1, defaultValue: "" })}
            />
            {carouselSlideCount > 1 && (
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  padding: 12,
                  display: "flex",
                  justifyContent: "center",
                  gap: 6,
                }}
              >
                {Array.from({ length: carouselSlideCount }, (_, i) => i).map((i) => (
                  <div
                    key={i}
                    onClick={() => setCarouselIdx(i)}
                    style={{
                      width: i === carouselIdx ? 18 : 6,
                      height: 6,
                      borderRadius: 999,
                      background:
                        i === carouselIdx ? "var(--primary-600)" : "rgba(255,255,255,0.7)",
                      transition: "width 200ms",
                      cursor: "pointer",
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <DailyNutritionBanner />

      <HomeChallengesWidget />

      {isOrdering && !requiresBranchSelection && <HomeOffersCarousel />}

      {isOrdering && recommendedItems.length > 0 && (
        <RecommendedRail
          items={recommendedItems}
          onOpenItem={onOpenItem}
          branchClosed={isClosed}
          hidePrices={requiresBranchSelection}
        />
      )}

      {/* Sticky search + category chips */}
      <div
        style={{
          position: "sticky",
          top: 0,
          background: "var(--surface-bg)",
          zIndex: 10,
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div style={{ padding: "10px 16px 8px" }}>
          <div
            className="search-bar"
            onClick={() => {
              saveHomeScroll();
              setSearchActive(true);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              height: 42,
              padding: "0 14px",
              background: "var(--surface-2)",
              borderRadius: "var(--radius-card)",
              border: "1px solid var(--border)",
              cursor: "text",
            }}
          >
            <Icons.search size={18} style={{ color: "var(--ink-3)" }} />
            <span style={{ flex: 1, fontSize: 14, color: "var(--ink-3)" }}>
              {t("home.search_placeholder")}
            </span>
            <Icons.filter size={18} style={{ color: "var(--ink-3)" }} />
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: 8,
            overflowX: "auto",
            padding: "0 16px 10px",
          }}
        >
          {sections.map((c) => {
            const on = activeCat === c.id;
            return (
              <button
                key={c.id}
                ref={(el) => {
                  chipRefs.current[c.id] = el;
                }}
                className="cat-chip"
                onClick={(e) => {
                  e.preventDefault();
                  jumpTo(c.id);
                }}
                style={{
                  padding: "8px 14px",
                  borderRadius: "var(--radius-chip)",
                  fontSize: 13,
                  fontWeight: 600,
                  background: on ? "var(--ink-1)" : "var(--surface-1)",
                  color: on ? "var(--surface-1)" : "var(--ink-1)",
                  border: `1px solid ${on ? "var(--ink-1)" : "var(--border)"}`,
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  flexShrink: 0,
                }}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Menu sections */}
      <div style={{ padding: "8px 16px 24px" }}>
        {requiresBranchSelection && !isMenuOnly && (
          <BranchSelectionPrompt
            onDelivery={() => navigate("/specify-location?returnTo=home")}
            onPickup={() => setChooserMode("pickup")}
          />
        )}
        {loading && sections.length === 0 ? (
          <MenuSkeleton />
        ) : error && sections.length === 0 ? (
          <MenuErrorState onRetry={() => window.location.reload()} />
        ) : (
          sections.map((s, i) => (
            <div
              key={s.id}
              ref={(el) => {
                sectionRefs.current[s.id] = el;
              }}
              data-section={s.id}
              style={{ scrollMarginTop: 110, paddingTop: i === 0 ? 4 : 18 }}
            >
              <SectionHeader
                eyebrow={s.id === "popular" ? "Most ordered" : null}
                title={s.label}
                sub={s.id === "popular" ? "Tried & loved by everyone" : null}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {s.items.map((it) => (
                  <ItemRow
                    key={`${s.id}-${it.id}`}
                    item={it}
                    onOpen={() => onOpenItem(it)}
                    branchClosed={isClosed}
                    hidePrice={requiresBranchSelection}
                    avgRating={itemRatings[it.id]?.avgRating}
                    reviewCount={itemRatings[it.id]?.count}
                  />
                ))}
              </div>
            </div>
          ))
        )}
        {sections.length > 0 && <div style={{ height: "60vh" }} />}
      </div>

      {/* Group cart mini bar — replaces the local cart bar when a group is
          active. Tapping it routes to /cart, which renders GroupCartView
          with the Fire-to-kitchen CTA at the bottom. */}
      {groupCart.status === "active" && (groupCart.cart?.draftLines.length ?? 0) > 0 && (
        <StickyBar
          style={{
            background: "var(--primary-600)",
            color: "#fff",
            borderTop: 0,
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 76px)",
            borderRadius: "var(--radius-card)",
            padding: 0,
            left: 16,
            right: 16,
            boxShadow: "0 14px 30px -10px rgba(0,0,0,0.35)",
          }}
        >
          <button
            onClick={() => navigate("/cart")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              width: "100%",
              padding: "14px 16px",
              background: "transparent",
              border: 0,
              color: "#fff",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 999,
                background: "rgba(255,255,255,0.2)",
                display: "grid",
                placeItems: "center",
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              {groupCart.cart?.draftLines.length ?? 0}
            </div>
            <div style={{ flex: 1, textAlign: "start" }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>
                {t("v2.group_cart.view_group_cart", {
                  defaultValue: "Review & place group order",
                })}
              </div>
              <div style={{ fontSize: 12, opacity: 0.85 }}>
                {t("v2.home.items_other", {
                  count: groupCart.cart?.draftLines.length ?? 0,
                })}
              </div>
            </div>
            <Icons.chevron size={18} />
          </button>
        </StickyBar>
      )}

      {/* Shortlist bar — the menu-only counterpart to the mini cart bar. */}
      {isMenuOnly && <ShortlistBar />}

      {/* Mini cart bar */}
      {isOrdering && groupCart.status !== "active" && cartCount > 0 && (
        <StickyBar
          style={{
            background: "var(--cart-bg)",
            color: "var(--cart-fg)",
            borderTop: 0,
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 76px)",
            borderRadius: "var(--radius-card)",
            padding: 0,
            left: 16,
            right: 16,
            boxShadow: "0 14px 30px -10px rgba(0,0,0,0.35)",
          }}
        >
          <button
            onClick={() => navigate("/cart")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              width: "100%",
              padding: "14px 16px",
              background: "transparent",
              border: 0,
              color: "var(--cart-fg)",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 999,
                background: "rgba(255,255,255,0.15)",
                display: "grid",
                placeItems: "center",
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              {cartCount}
            </div>
            <div style={{ flex: 1, textAlign: "start" }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{t("v2.home.view_cart")}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 2 }}>
                <div style={{ fontSize: 12, opacity: 0.7 }}>
                  {t(cartCount === 1 ? "v2.home.items_one" : "v2.home.items_other", { count: cartCount })}
                </div>
                {shownPromotion && (
                  <div
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      padding: "2px 6px",
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 4
                    }}
                  >
                    <Icons.check size={10} />
                    {shownPromotion.promoCode ?? t("v2.cart.promo")}
                  </div>
                )}
              </div>
            </div>
            <div className="num" style={{ fontSize: 14, fontWeight: 800, whiteSpace: "nowrap" }}><Price amount={cartTotal} />
            </div>
            <Icons.chevron size={18} />
          </button>
        </StickyBar>
      )}
      <FulfillmentChooser
        openMode={chooserMode}
        onClose={() => setChooserMode(null)}
        addresses={addresses}
        addressesLoading={addressesLoading}
        selectedAddressId={selectedAddressId}
        onSelectAddress={(id) => {
          setMode("delivery");
          const address = addresses.find((a) => a.id === id);
          if (address) void onSelectDeliveryAddress(address);
        }}
        onAddAddress={() => {
          setChooserMode(null);
          navigate("/specify-location?returnTo=new-address-form&next=home");
        }}
        branches={chooserBranches}
        selectedBranchId={requiresBranchSelection ? null : (selectedBranchId ?? branchId)}
        lang={lang}
        onSelectBranch={(id, selected) => {
          setMode("pickup");
          selectBranch(id, selected);
          setChooserMode(null);
        }}
      />
      {isOrdering && (
        <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
      )}
    </div>
  );
}

function FulfillmentChooser({
  openMode,
  onClose,
  addresses,
  addressesLoading,
  selectedAddressId,
  onSelectAddress,
  onAddAddress,
  branches,
  selectedBranchId,
  lang,
  onSelectBranch,
}: {
  openMode: FulfillmentMode | null;
  onClose: () => void;
  addresses: AppSchemasCustomerAddress[];
  addressesLoading: boolean;
  selectedAddressId: string | null;
  onSelectAddress: (id: string) => void;
  onAddAddress: () => void;
  branches: BranchConfig[];
  selectedBranchId: string | null;
  lang: string;
  onSelectBranch: (id: string, branch: BranchConfig) => void;
}) {
  const { t } = useTranslation();
  const [host, setHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setHost(document.querySelector<HTMLElement>(".phone-shell"));
  }, []);

  // Close on Escape for keyboard users.
  useEffect(() => {
    if (!openMode) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openMode, onClose]);

  if (!openMode || !host) return null;

  const title =
    openMode === "delivery"
      ? t("v2.home.choose_delivery_address")
      : t("v2.home.choose_pickup_branch");

  return createPortal(
    <>
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(10, 14, 12, 0.42)",
          zIndex: 40,
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          maxHeight: "78%",
          background: "var(--surface-1)",
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: "14px 16px calc(env(safe-area-inset-bottom, 0px) + 18px)",
          boxShadow: "0 -18px 42px -18px rgba(0,0,0,0.38)",
          zIndex: 41,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          aria-hidden
          style={{
            width: 40,
            height: 4,
            borderRadius: 999,
            background: "var(--surface-2-strong)",
            margin: "2px auto 14px",
          }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1, fontSize: 18, fontWeight: 800, color: "var(--ink-1)" }}>
            {title}
          </div>
          <button
            onClick={onClose}
            aria-label={t("v2.common.cancel")}
            style={{
              width: 34,
              height: 34,
              borderRadius: 999,
              border: "1px solid var(--border)",
              background: "var(--surface-2)",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              color: "var(--ink-2)",
            }}
          >
            <Icons.close size={16} />
          </button>
        </div>

        <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
          {openMode === "delivery" ? (
            <>
              {addressesLoading && addresses.length === 0 && (
                <div style={{ padding: 14, color: "var(--ink-3)", fontSize: 13 }}>
                  {t("v2.checkout.loading_addresses")}
                </div>
              )}
              {!addressesLoading && addresses.length === 0 && (
                <div style={{ padding: 14, color: "var(--ink-2)", fontSize: 13 }}>
                  {t("v2.checkout.no_addresses")}
                </div>
              )}
              {addresses.map((address) => (
                <button
                  key={address.id}
                  onClick={() => onSelectAddress(address.id)}
                  style={{
                    width: "100%",
                    border: `1px solid ${
                      selectedAddressId === address.id
                        ? "var(--primary-600)"
                        : "var(--border)"
                    }`,
                    background:
                      selectedAddressId === address.id
                        ? "var(--primary-50)"
                        : "var(--surface-1)",
                    borderRadius: 12,
                    padding: 12,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    textAlign: "start",
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  <Icons.pin size={17} style={{ color: "var(--primary-700)", marginTop: 2 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink-1)" }}>
                      {address.name || t("v2.checkout.address_label_default")}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 3 }}>
                      {addressDisplayLine(address)}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>
                      {[address.city, address.state, address.postalCode].filter(Boolean).join(", ")}
                    </div>
                  </div>
                  {address.isPrimary && <Badge tone="primary">{t("v2.common.default")}</Badge>}
                </button>
              ))}
              <Button full variant="outline" onClick={onAddAddress} leading={<Icons.plus size={16} />}>
                {t("v2.address.add_new")}
              </Button>
            </>
          ) : (
            <>
              {branches.length === 0 && (
                <div style={{ padding: 14, color: "var(--ink-2)", fontSize: 13 }}>
                  {t("v2.home.no_pickup_branches")}
                </div>
              )}
              {branches.map((branch) => {
                const id = branchIdentifier(branch);
                return (
                  <button
                    key={id}
                    onClick={() => onSelectBranch(id, branch)}
                    style={{
                      width: "100%",
                      border: `1px solid ${
                        selectedBranchId === id
                          ? "var(--primary-600)"
                          : "var(--border)"
                      }`,
                      background:
                        selectedBranchId === id
                          ? "var(--primary-50)"
                          : "var(--surface-1)",
                      borderRadius: 12,
                      padding: 12,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      textAlign: "start",
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    <Icons.store size={17} style={{ color: "var(--primary-700)", marginTop: 2 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink-1)" }}>
                        {branchDisplayName(branch, lang)}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 3 }}>
                        {branchAddressLine(branch, lang) || id}
                      </div>
                    </div>
                  </button>
                );
              })}
            </>
          )}
        </div>
      </div>
    </>,
    host,
  );
}

function BranchSelectionPrompt({
  onDelivery,
  onPickup,
}: {
  onDelivery: () => void;
  onPickup: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Card padding={20} style={{ textAlign: "start" }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: "var(--primary-50)",
          color: "var(--primary-700)",
          display: "grid",
          placeItems: "center",
          marginBottom: 12,
        }}
      >
        <Icons.pin size={20} />
      </div>
      <div style={{ fontSize: 17, fontWeight: 800, color: "var(--ink-1)" }}>
        {t("v2.home.select_branch_title")}
      </div>
      <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5, marginTop: 6 }}>
        {t("v2.home.select_branch_desc")}
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <Button onClick={onDelivery} leading={<Icons.pin size={16} />} style={{ flex: 1 }}>
          {t("v2.home.set_delivery_location")}
        </Button>
        <Button
          onClick={onPickup}
          variant="outline"
          leading={<Icons.store size={16} />}
          style={{ flex: 1 }}
        >
          {t("v2.home.pickup")}
        </Button>
      </div>
    </Card>
  );
}

function ActiveBranchStrip({
  name,
  address,
  isPreview,
  isClosed,
  minOrder = 0,
  hideChange = false,
  reviewSummary = null,
  onChange,
}: {
  name: string;
  address: string;
  isPreview: boolean;
  isClosed: boolean;
  minOrder?: number;
  hideChange?: boolean;
  reviewSummary?: BranchReviewSummary | null;
  onChange: () => void;
}) {
  const { t } = useTranslation();
  const avgRating =
    reviewSummary && reviewSummary.count > 0 ? Math.round(reviewSummary.avgRating * 10) / 10 : null;
  return (
    <div style={{ padding: "10px 16px 0", background: "var(--surface-bg)" }}>
      <Card
        padding={12}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          borderRadius: 12,
          boxShadow: "none",
        }}
      >
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: isPreview ? "var(--primary-50)" : "var(--surface-2)",
            color: isPreview ? "var(--primary-700)" : "var(--ink-2)",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <Icons.store size={17} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--ink-3)",
                textTransform: "uppercase",
                letterSpacing: 0.3,
                whiteSpace: "nowrap",
              }}
            >
              {isPreview ? t("v2.home.previewing_branch") : t("v2.home.active_branch")}
            </div>
            <Badge tone={isClosed ? "danger" : "success"}>
              {isClosed ? t("v2.home.closed") : t("v2.home.open")}
            </Badge>
            {minOrder > 0 && (
              <Badge tone="neutral">
                {t("v2.home.min_order", { amount: minOrder })}
              </Badge>
            )}
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 800,
              color: "var(--ink-1)",
              marginTop: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            {address && (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--ink-2)",
                  marginTop: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {address}
              </div>
            )}
            {avgRating != null && (
              <div
                style={{ display: "flex", alignItems: "center", gap: 3, marginTop: 2, flexShrink: 0 }}
                aria-label={
                  t(
                    "v2.reviews.public.branch_rating_aria",
                    "This location is rated {{rating}} out of 5 from {{count}} reviews",
                    { rating: avgRating, count: reviewSummary!.count },
                  ) as string
                }
              >
                <Icons.star size={12} style={{ color: "var(--primary-600)" }} />
                <span className="num" style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-1)" }}>
                  {avgRating}
                </span>
                <span style={{ fontSize: 11, color: "var(--ink-3)" }}>({reviewSummary!.count})</span>
              </div>
            )}
          </div>
        </div>
        {!hideChange && (
          <button
            onClick={onChange}
            style={{
              border: "1px solid var(--border)",
              background: "var(--surface-1)",
              color: "var(--ink-1)",
              borderRadius: "var(--radius-chip)",
              padding: "8px 11px",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
              flexShrink: 0,
            }}
          >
            {isPreview ? t("v2.home.set_location_short") : t("v2.common.change")}
          </button>
        )}
      </Card>
    </div>
  );
}

function DailyNutritionBanner() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  return (
    <div style={{ padding: "8px 16px 0" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: "var(--primary-50)",
          borderInlineStart: "4px solid var(--primary-500)",
          borderRadius: 10,
          padding: "11px 12px",
        }}
      >
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 999,
            background: "var(--surface-1)",
            display: "grid",
            placeItems: "center",
            color: "var(--primary-700)",
            flexShrink: 0,
          }}
        >
          <Icons.info size={16} />
        </div>
        <div
          style={{
            fontSize: 12,
            lineHeight: 1.55,
            color: "var(--primary-700)",
            fontWeight: 600,
            textAlign: isRtl ? "right" : "left",
          }}
        >
          {t("average_daily_intake")}
          <strong>{t("2000_kcal")}</strong>
          {" "}
          {t("varies_by_age_gender_activity")}
        </div>
      </div>
    </div>
  );
}

function RecommendedRail({
  items,
  onOpenItem,
  branchClosed,
  hidePrices,
}: {
  items: MenuItem[];
  onOpenItem: (it: MenuItem) => void;
  branchClosed: boolean;
  hidePrices: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ padding: "16px 0 4px" }}>
      <div style={{ padding: "0 16px", marginBottom: 10 }}>
        <SectionHeader title={t("recommended")} />
      </div>
      <div
        style={{
          display: "flex",
          gap: 12,
          overflowX: "auto",
          padding: "0 16px 4px",
        }}
      >
        {items.map((item) => {
          const unavailable = !item.isAvailable || branchClosed;
          const showPriceAfterOptionsNote = shouldShowPriceAfterOptionsNote(item);
          return (
            <button
              key={item.id}
              onClick={() => onOpenItem(item)}
              style={{
                width: 154,
                flex: "0 0 154px",
                border: "1px solid var(--border)",
                background: "var(--card-bg)",
                borderRadius: "var(--radius-card)",
                boxShadow: "var(--card-shadow)",
                padding: 0,
                overflow: "hidden",
                textAlign: "start",
                cursor: "pointer",
                fontFamily: "inherit",
                opacity: unavailable ? 0.62 : 1,
              }}
            >
              <FoodImg
                tone={item.tone}
                rounded={0}
                label={item.id}
                src={item.imageUrl}
                alt={item.name}
                style={{ aspectRatio: "1.25 / 1" }}
              />
              <div style={{ padding: 10 }}>
                <div
                  className="display"
                  style={{
                    fontSize: 13,
                    color: "var(--ink-1)",
                    lineHeight: 1.25,
                    minHeight: 34,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {item.name}
                </div>
                <div style={{ marginTop: 7, display: "flex", alignItems: "center", gap: 8 }}>
                  {hidePrices ? (
                    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-3)" }}>
                      {t("v2.home.select_branch_for_price")}
                    </span>
                  ) : showPriceAfterOptionsNote ? (
                    <span style={{ fontSize: 10, fontWeight: 600, color: "var(--ink-3)", lineHeight: 1.25 }}>
                      {t("v2.home.price_after_options")}
                    </span>
                  ) : (
                    <div className="num" style={{ fontSize: 13, fontWeight: 800 }}>
                      <Price amount={item.price} />
                    </div>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ItemRow({
  item,
  onOpen,
  onAddDirectly,
  branchClosed = false,
  hidePrice = false,
  avgRating,
  reviewCount,
}: {
  item: MenuItem;
  onOpen: () => void;
  onAddDirectly?: () => void;
  branchClosed?: boolean;
  hidePrice?: boolean;
  /** Public rating badge (M4) — fetched once at the list level, not per-card. */
  avgRating?: number;
  reviewCount?: number;
}) {
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const { addToShortlist } = useShortlist();
  const { isMenuOnly } = useAppMode();
  const groupCart = useGroupCart();
  const soldOut = !item.isAvailable;
  const hasVariantsOrModifiers = itemHasVariantsOrModifiers(item);
  const showPriceAfterOptionsNote = shouldShowPriceAfterOptionsNote(item);
  // Opening hours gate ordering, not browsing — a closed branch must not stop
  // someone shortlisting from the menu.
  const addDisabled =
    soldOut || (branchClosed && !isMenuOnly) || hidePrice;
  const calories =
    typeof item.raw?.nutritionalInformation?.calories === "number"
      ? item.raw.nutritionalInformation.calories
      : item.cal;
  const highSodium = !!item.raw?.nutritionalInformation?.highSodiumWarning;
  return (
    <Card
      padding={0}
      onClick={onOpen}
      style={{
        display: "grid",
        gridTemplateColumns: "112px 1fr",
        gap: 0,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "relative",
          padding: 10,
          opacity: soldOut ? 0.5 : 1,
          filter: soldOut ? "grayscale(1)" : "none",
        }}
      >
        <FoodImg
          tone={item.tone}
          square
          rounded={12}
          label={item.id}
          src={item.imageUrl}
          alt={item.name}
        />
      </div>
      <div
        style={{
          padding: "12px 14px 12px 6px",
          display: "flex",
          flexDirection: "column",
          gap: 4,
          minWidth: 0,
          opacity: soldOut ? 0.5 : 1,
        }}
      >
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {item.badges?.slice(0, 2).map((b) => (
            <Badge
              key={b}
              tone={
                b === "Popular" || b === "Chef's pick"
                  ? "primary"
                  : b === "Spicy"
                  ? "danger"
                  : "neutral"
              }
            >
              {b === "Spicy" && <Icons.flame size={10} />}
              {b === "Vegan" || b === "Veg" ? <Icons.leaf size={10} /> : null}
              {b}
            </Badge>
          ))}
        </div>
        <div
          className="display"
          style={{
            fontSize: 15,
            color: "var(--ink-1)",
            lineHeight: 1.2,
          }}
        >
          {item.name}
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: "var(--ink-2)",
            lineHeight: 1.4,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {item.desc}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 4,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {hidePrice ? (
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-3)" }}>
                {t("v2.home.select_branch_for_price")}
              </span>
            ) : showPriceAfterOptionsNote ? (
              <span style={{ fontSize: 10, fontWeight: 600, color: "var(--ink-3)", lineHeight: 1.25 }}>
                {t("v2.home.price_after_options")}
              </span>
            ) : (
              <div className="num" style={{ fontSize: 13, fontWeight: 800 }}>
                <Price amount={item.price} />
              </div>
            )}
            {avgRating != null && reviewCount != null && reviewCount > 0 && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  background: "var(--card-bg)",
                  border: "1px solid var(--border)",
                  padding: "2px 6px",
                  borderRadius: 6,
                }}
                aria-label={
                  t(
                    "v2.reviews.public.item_rating_aria",
                    "This item is rated {{rating}} out of 5 from {{count}} reviews",
                    { rating: avgRating, count: reviewCount },
                  ) as string
                }
              >
                <Icons.star
                  size={12}
                  style={{ color: "#f59e0b", fill: "#f59e0b" }}
                />
                <span
                  className="num"
                  style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-1)", lineHeight: 1 }}
                >
                  {avgRating}
                </span>
                <span
                  className="num"
                  style={{ fontSize: 11, fontWeight: 500, color: "var(--ink-3)", marginLeft: 2, lineHeight: 1 }}
                >
                  ({reviewCount})
                </span>
              </div>
            )}
            {highSodium && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  background: "#fff0f2",
                  color: "#f43f5e",
                  padding: "4px 8px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                <img src={saltIcon} alt="High Sodium" style={{ width: 14, height: 14 }} />
                {t("high_sodium")}
              </div>
            )}
            {calories > 0 && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  background: "#fff7ed",
                  color: "#f97316",
                  padding: "4px 8px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                <img src={flameIcon} alt="Calories" style={{ width: 14, height: 14 }} />
                <span className="num">{t("v2.home.cal", { count: calories })}</span>
              </div>
            )}
          </div>
          {soldOut ? (
            <Badge tone="neutral">{t("v2.home.sold_out")}</Badge>
          ) : (
            <button
              data-testid="menu-add"
              data-item-id={item.id}
              data-item-name={item.name}
              onClick={(e) => {
                e.stopPropagation();
                if (addDisabled) return;

                if (!hasVariantsOrModifiers) {
                  const line = {
                    id: `${item.id}-${Date.now()}`,
                    itemId: item.id,
                    name: item.name,
                    categoryId: item.cat,
                    categoryName: item.categoryName,
                    price: item.price,
                    qty: 1,
                    tone: item.tone,
                    imageUrl: item.imageUrl,
                    selectedModifiers: [],
                    selectedVariants: [],
                    modsLabel: [],
                    notes: "",
                  };
                  // Menu-only stays on the menu: the shortlist bar reflects
                  // the add, and `onAddDirectly` navigates to /cart, which the
                  // route guard would bounce straight back here anyway.
                  void quickAdd(
                    { ...line, categoryNameRaw: item.categoryNameRaw },
                    {
                      isMenuOnly,
                      groupActive:
                        groupCart.status === "active" && !!groupCart.cart,
                      addToShortlist,
                      addToCart,
                      addGroupLine: groupCart.addLine,
                    },
                  )
                    .then(() => {
                      if (!isMenuOnly) onAddDirectly?.();
                    })
                    .catch((err) =>
                      console.warn("[menu] group addLine failed", err),
                    );
                } else {
                  onOpen();
                }
              }}
              disabled={addDisabled}
              aria-disabled={addDisabled}
              style={{
                width: 34,
                height: 34,
                borderRadius: 999,
                border: 0,
                background: addDisabled
                  ? "var(--surface-2)"
                  : "var(--primary-600)",
                color: addDisabled ? "var(--ink-3)" : "#fff",
                display: "grid",
                placeItems: "center",
                cursor: addDisabled ? "not-allowed" : "pointer",
                boxShadow: addDisabled
                  ? "none"
                  : "0 4px 10px -3px rgba(11,170,87,0.5)",
              }}
            >
              <Icons.plus size={16} />
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

function MenuSkeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 4 }}>
      {[0, 1, 2].map((i) => (
        <Card
          key={i}
          padding={0}
          style={{
            display: "grid",
            gridTemplateColumns: "112px 1fr",
            gap: 0,
            overflow: "hidden",
          }}
        >
          <div style={{ padding: 10 }}>
            <div
              style={{
                width: "100%",
                aspectRatio: "1 / 1",
                borderRadius: 12,
                background: "var(--surface-2)",
              }}
            />
          </div>
          <div
            style={{
              padding: "12px 14px 12px 6px",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              minWidth: 0,
            }}
          >
            <div style={{ height: 10, width: "40%", background: "var(--surface-2)", borderRadius: 4 }} />
            <div style={{ height: 14, width: "70%", background: "var(--surface-2)", borderRadius: 4 }} />
            <div style={{ height: 10, width: "90%", background: "var(--surface-2)", borderRadius: 4 }} />
            <div style={{ height: 10, width: "80%", background: "var(--surface-2)", borderRadius: 4 }} />
            <div style={{ height: 14, width: "30%", background: "var(--surface-2)", borderRadius: 4, marginTop: 4 }} />
          </div>
        </Card>
      ))}
    </div>
  );
}

function MenuErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <Card padding={28} style={{ textAlign: "center" }}>
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 999,
          background: "var(--surface-2)",
          display: "grid",
          placeItems: "center",
          margin: "0 auto 12px",
          color: "var(--ink-3)",
        }}
      >
        <Icons.info size={22} />
      </div>
      <div style={{ fontWeight: 700, fontSize: 15 }}>{t("v2.home.couldnt_load_menu")}</div>
      <div style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 4, marginBottom: 14 }}>
        {t("v2.home.couldnt_load_menu_desc")}
      </div>
      <Button onClick={onRetry} variant="primary" size="md">
        {t("v2.common.retry")}
      </Button>
    </Card>
  );
}

function SearchOverlay({
  q,
  setQ,
  onClose,
  onOpenItem,
  results,
  allItems,
  categories,
  branchClosed,
  hidePrices,
  branchSlug,
  onCategorySelect,
  itemRatings = {},
}: {
  q: string;
  setQ: (s: string) => void;
  onClose: () => void;
  onOpenItem: (it: MenuItem) => void;
  results: MenuItem[];
  allItems: MenuItem[];
  categories: { id: string; label: string }[];
  branchClosed?: boolean;
  hidePrices?: boolean;
  branchSlug: string | null | undefined;
  onCategorySelect: (id: string) => void;
  itemRatings?: Record<string, ItemReviewSummary>;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const recents = useMemo(() => readRecentSearches(branchSlug), [branchSlug]);
  const popular = allItems.filter((m) => m.popular).slice(0, 4);
  const cats = categories.filter((c) => c.id !== "popular");

  return (
    <div style={{ paddingBottom: 24, minHeight: "100%", background: "var(--surface-bg)" }}>
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "var(--surface-1)",
          padding: "12px 16px",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <button
          onClick={onClose}
          style={{
            width: 36,
            height: 36,
            display: "grid",
            placeItems: "center",
            background: "transparent",
            border: 0,
            cursor: "pointer",
            color: "var(--ink-1)",
            flexShrink: 0,
          }}
        >
          <Icons.arrowLeft size={20} />
        </button>
        <div
          className="search-bar"
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 8,
            height: 42,
            padding: "0 14px",
            background: "var(--surface-2)",
            borderRadius: "var(--radius-card)",
            border: "1px solid var(--border)",
          }}
        >
          <Icons.search size={18} style={{ color: "var(--ink-3)" }} />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("v2.home.search_input_placeholder")}
            style={{
              flex: 1,
              border: 0,
              background: "transparent",
              outline: "none",
              fontSize: 14,
              color: "var(--ink-1)",
              fontFamily: "inherit",
            }}
          />
          {q && (
            <button
              onClick={() => setQ("")}
              style={{
                background: "transparent",
                border: 0,
                color: "var(--ink-3)",
                cursor: "pointer",
                padding: 4,
                display: "grid",
                placeItems: "center",
              }}
            >
              <Icons.close size={16} />
            </button>
          )}
        </div>
      </div>

      {!q && (
        <div style={{ padding: 16 }}>
          {recents.length > 0 && (
            <div style={{ marginBottom: 18 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--ink-3)",
                letterSpacing: 0.3,
                textTransform: "uppercase",
                marginBottom: 8,
              }}
            >
              {t("v2.home.search_recent")}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {recents.map((r) => (
                <button
                  key={r}
                  onClick={() => setQ(r)}
                  style={{
                    padding: "8px 14px",
                    borderRadius: "var(--radius-chip)",
                    fontSize: 13,
                    fontWeight: 500,
                    background: "var(--surface-1)",
                    color: "var(--ink-1)",
                    border: "1px solid var(--border)",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Icons.clock size={12} style={{ color: "var(--ink-3)" }} />
                  {r}
                </button>
              ))}
            </div>
            </div>
          )}

          <div style={{ marginBottom: 18 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--ink-3)",
                letterSpacing: 0.3,
                textTransform: "uppercase",
                marginBottom: 8,
              }}
            >
              {t("v2.home.search_browse_categories")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {cats.map((c) => (
                <button
                  key={c.id}
                  onClick={() => onCategorySelect(c.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: 14,
                    background: "var(--card-bg)",
                    border: "var(--card-border)",
                    boxShadow: "var(--card-shadow)",
                    borderRadius: "var(--radius-card)",
                    cursor: "pointer",
                    textAlign: "start",
                    fontFamily: "inherit",
                    color: "var(--ink-1)",
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: "var(--primary-50)",
                      display: "grid",
                      placeItems: "center",
                      color: "var(--primary-700)",
                    }}
                  >
                    <Icons.bag size={16} />
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{c.label}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--ink-3)",
                letterSpacing: 0.3,
                textTransform: "uppercase",
                marginBottom: 8,
              }}
            >
              {t("v2.home.search_popular_now")}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {popular.map((it) => (
                <ItemRow
                  key={it.id}
                  item={it}
                  onOpen={() => onOpenItem(it)}
                  onAddDirectly={() => navigate("/cart")}
                  branchClosed={branchClosed}
                  hidePrice={hidePrices}
                  avgRating={itemRatings[it.id]?.avgRating}
                  reviewCount={itemRatings[it.id]?.count}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {q && (
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 10 }}>
            {t(results.length === 1 ? "v2.home.search_results_one" : "v2.home.search_results_other", { count: results.length, q })}
          </div>
          {results.length === 0 ? (
            <Card padding={28} style={{ textAlign: "center" }}>
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 999,
                  background: "var(--surface-2)",
                  display: "grid",
                  placeItems: "center",
                  margin: "0 auto 12px",
                  color: "var(--ink-3)",
                }}
              >
                <Icons.search size={22} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{t("v2.home.search_no_matches")}</div>
              <div style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 4 }}>
                {t("v2.home.search_try_keyword")}
              </div>
            </Card>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {results.map((it) => (
                <ItemRow
                  key={it.id}
                  item={it}
                  onOpen={() => onOpenItem(it)}
                  onAddDirectly={() => navigate("/cart")}
                  branchClosed={branchClosed}
                  hidePrice={hidePrices}
                  avgRating={itemRatings[it.id]?.avgRating}
                  reviewCount={itemRatings[it.id]?.count}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Promo carousel image — rendered at ~360px wide x 16:7 (~157px tall), so a
// 400x400 thumbnail is plenty. Falls back to the original URL if the thumb
// is missing (older menus uploaded before the thumbnail pipeline ran).
function CarouselImage({ src, alt }: { src: string; alt: string }) {
  const [useOriginal, setUseOriginal] = useState(false);
  const thumb = useMemo(() => imageThumbnailUrl(src, "400x400"), [src]);
  // Normalize the fallback too. Raw `src` is a firebasestorage URL, which
  // bypasses Cloud CDN and is billed as egress — and this is the largest image
  // on the page, fetched on every visit. Same fallback trap as FoodImg.
  const original = useMemo(() => normalizeImageUrl(src) ?? src, [src]);
  const display = useOriginal || !thumb ? original : thumb;
  return (
    <img
      src={display}
      alt={alt}
      onError={() => {
        if (!useOriginal) setUseOriginal(true);
      }}
      style={{
        display: "block",
        width: "100%",
        aspectRatio: "16 / 7",
        objectFit: "cover",
      }}
    />
  );
}
